"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";

interface RevealTextProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "down" | "left" | "right";
}

export function RevealText({
  children,
  className = "",
  delay = 0,
  direction = "up",
}: RevealTextProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const variants = {
    up: { initial: { y: 80, opacity: 0 }, animate: { y: 0, opacity: 1 } },
    down: { initial: { y: -80, opacity: 0 }, animate: { y: 0, opacity: 1 } },
    left: { initial: { x: -80, opacity: 0 }, animate: { x: 0, opacity: 1 } },
    right: { initial: { x: 80, opacity: 0 }, animate: { x: 0, opacity: 1 } },
  };

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        initial={variants[direction].initial}
        animate={isInView ? variants[direction].animate : variants[direction].initial}
        transition={{
          duration: 0.8,
          delay,
          ease: [0.16, 1, 0.3, 1],
        }}
      >
        {children}
      </motion.div>
    </div>
  );
}

interface SplitTextProps {
  text: string;
  className?: string;
  charClassName?: string;
  delay?: number;
}

export function SplitText({
  text,
  className = "",
  charClassName = "",
  delay = 0,
}: SplitTextProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true" className="inline-flex flex-wrap">
        {text.split("").map((char, i) => (
          <motion.span
            key={i}
            className={`inline-block ${charClassName}`}
            initial={{ y: "100%", opacity: 0 }}
            animate={isInView ? { y: 0, opacity: 1 } : { y: "100%", opacity: 0 }}
            transition={{
              duration: 0.6,
              delay: delay + i * 0.03,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            {char === " " ? "\u00A0" : char}
          </motion.span>
        ))}
      </span>
    </div>
  );
}

interface WordRevealProps {
  text: string;
  className?: string;
  wordClassName?: string;
  delay?: number;
}

export function WordReveal({
  text,
  className = "",
  wordClassName = "",
  delay = 0,
}: WordRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const words = text.split(" ");

  return (
    <div ref={ref} className={className}>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true" className="flex flex-wrap">
        {words.map((word, i) => (
          <span key={i} className="overflow-hidden mr-[0.25em]">
            <motion.span
              className={`inline-block ${wordClassName}`}
              initial={{ y: "100%", opacity: 0 }}
              animate={isInView ? { y: 0, opacity: 1 } : { y: "100%", opacity: 0 }}
              transition={{
                duration: 0.6,
                delay: delay + i * 0.08,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              {word}
            </motion.span>
          </span>
        ))}
      </span>
    </div>
  );
}
